import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input } from 'rsuite';
import axios from 'axios';
import Swal from 'sweetalert2';
import { config } from '../../data/config';
import { getCookies } from 'cookies-next';
import { useTranslation } from 'react-i18next';
import BreadCrump from '../tools/breadcrump';
import HeaderOfPage from '../tools/headerOfPage';
import UserIcon from '@rsuite/icons/legacy/User';
import MailIcon from '@rsuite/icons/legacy/Envelope';
import PhoneIcon from '@rsuite/icons/legacy/Phone';
import ClockIcon from '@rsuite/icons/legacy/ClockO';
import MessageIcon from '@rsuite/icons/legacy/Comments';
import SendIcon from '@rsuite/icons/legacy/Send';
import ArrowLeftIcon from '@rsuite/icons/legacy/ArrowLeft';
import CheckCircleIcon from '@rsuite/icons/legacy/CheckCircle';
import AlertIcon from '@rsuite/icons/legacy/ExclamationTriangle';
import CloseIcon from '@rsuite/icons/Close';

export default function TicketDetail() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [replyName, setReplyName] = useState('Admin');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (id) {
            fetchTicketDetails();
        }
    }, [id]);

    const fetchTicketDetails = async () => {
        if (!id) return;
        
        setLoading(true);
        try {
            const cookie = getCookies();
            const response = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/tickets/${id}`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            if (response.data.success) {
                setTicket(response.data.message);
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error loading ticket'),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitReply = async () => {
        if (!replyText.trim() || !id) return;

        setIsSubmitting(true);
        try {
            const cookie = getCookies();
            await axios({
                method: 'put',
                url: `${config.url}/v1/dashboard/tickets/${id}`,
                data: {
                    message: replyText.trim(),
                    name: replyName.trim() || 'Admin'
                },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Reply added successfully'),
                timer: 1500,
                showConfirmButton: false
            });

            setReplyText('');
            await fetchTicketDetails();
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error adding reply'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStatusChange = async (newStatus: string) => {
        if (!id) return;

        try {
            const cookie = getCookies();
            await axios({
                method: 'put',
                url: `${config.url}/v1/dashboard/tickets/${id}`,
                data: { status: newStatus },
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Ticket status updated'),
                timer: 1500,
                showConfirmButton: false
            });

            await fetchTicketDetails();
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error updating ticket'),
            });
        }
    };

    const breadcrump = [
        {
            name: 'Dashboard',
            url: '/dashboard'
        },
        {
            name: 'Tickets',
            url: '/dashboard/Tickets'
        },
        {
            name: 'Ticket Detail',
            url: null
        },
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'answered':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'reviewing':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'closed':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (status: string): JSX.Element => {
        switch (status) {
            case 'answered':
                return <CheckCircleIcon className="w-4 h-4" />;
            case 'reviewing':
                return <AlertIcon className="w-4 h-4" />;
            case 'closed':
                return <CloseIcon className="w-4 h-4" />;
            default:
                return <AlertIcon className="w-4 h-4" />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'answered':
                return t('Answered');
            case 'reviewing':
                return t('Reviewing');
            case 'closed':
                return t('Closed');
            default:
                return status;
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump}></BreadCrump>
            <HeaderOfPage name="Ticket Detail"></HeaderOfPage>
            
            <div className="mt-6">
                <Button 
                    onClick={() => navigate('/dashboard/Tickets')} 
                    className="mb-4 flex items-center gap-2"
                    appearance="subtle"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    {t('Back') || '← Back'}
                </Button>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="text-gray-500 mt-4">{t('Loading...')}</p>
                    </div>
                ) : ticket ? (
                    <div className="space-y-6">
                        {/* Ticket Header Card */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="flex-1">
                                    <h1 className="text-2xl font-bold text-gray-900 mb-3">{ticket.title}</h1>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(ticket.status)}`}>
                                            {getStatusIcon(ticket.status)}
                                            {getStatusText(ticket.status)}
                                        </span>
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <ClockIcon className="w-4 h-4" />
                                            {new Date(ticket.date || ticket.createdAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </div>
                                    </div>
                                </div>
                                {ticket.status !== 'closed' && (
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('Status')}:</label>
                                        <select
                                            value={ticket.status}
                                            onChange={(e) => handleStatusChange(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="reviewing">{t('Reviewing')}</option>
                                            <option value="answered">{t('Answered')}</option>
                                            <option value="closed">{t('Closed')}</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* User Information Card */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <UserIcon className="w-5 h-5 text-gray-600" />
                                {t('User Information')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <UserIcon className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('Name')}</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {ticket.user?.name || ticket.name || 'N/A'} {ticket.user?.lastname || ticket.lastname || ''}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <MailIcon className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('User Email')}</p>
                                        <p className="text-sm font-medium text-gray-900 break-all">
                                            {ticket.user?.email || ticket.email || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <PhoneIcon className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('User Phone')}</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {ticket.user?.phone || ticket.phone || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Conversation Thread */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                <MessageIcon className="w-5 h-5 text-gray-600" />
                                {t('Conversation')}
                            </h3>
                            
                            <div className="space-y-4">
                                {/* Original Message */}
                                <div className="relative pl-4 border-l-4 border-gray-400">
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-gray-200 rounded-full">
                                                    <UserIcon className="w-4 h-4 text-gray-700" />
                                                </div>
                                                <span className="font-semibold text-gray-900">
                                                    {ticket.user?.name || ticket.name || 'User'}
                                                </span>
                                                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                                                    {t('Original Message')}
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                                <ClockIcon className="w-3 h-3" />
                                                {new Date(ticket.date || ticket.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                        <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                                            {ticket.description || ticket.data?.message || 'No message'}
                                        </div>
                                    </div>
                                </div>

                                {/* Replies */}
                                {ticket.replies && ticket.replies.length > 0 && (
                                    ticket.replies.map((reply: any, index: number) => (
                                        <div 
                                            key={reply.id || index}
                                            className={`relative pl-4 border-l-4 ${
                                                reply.isAdmin
                                                    ? 'border-blue-500'
                                                    : 'border-gray-300'
                                            }`}
                                        >
                                            <div className={`rounded-lg p-4 ${
                                                reply.isAdmin
                                                    ? 'bg-blue-50'
                                                    : 'bg-gray-50'
                                            }`}>
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`p-2 rounded-full ${
                                                            reply.isAdmin
                                                                ? 'bg-blue-200'
                                                                : 'bg-gray-200'
                                                        }`}>
                                                            <UserIcon className={`w-4 h-4 ${
                                                                reply.isAdmin
                                                                    ? 'text-blue-700'
                                                                    : 'text-gray-700'
                                                            }`} />
                                                        </div>
                                                        <span className={`font-semibold ${
                                                            reply.isAdmin ? 'text-blue-900' : 'text-gray-900'
                                                        }`}>
                                                            {reply.name || (reply.isAdmin ? 'Support' : 'User')}
                                                        </span>
                                                        {reply.isAdmin && (
                                                            <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full font-medium">
                                                                {t('Admin')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                                        <ClockIcon className="w-3 h-3" />
                                                        {new Date(reply.date).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                                <div className={`text-sm leading-relaxed whitespace-pre-wrap ${
                                                    reply.isAdmin ? 'text-blue-900' : 'text-gray-700'
                                                }`}>
                                                    {reply.text}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Reply Form */}
                        {ticket.status !== 'closed' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <SendIcon className="w-5 h-5 text-gray-600" />
                                    {t('Add Reply')}
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Reply Name')}
                                        </label>
                                        <Input
                                            placeholder={t('Reply Name')}
                                            value={replyName}
                                            onChange={(val) => setReplyName(val)}
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Reply Text')}
                                        </label>
                                        <textarea
                                            placeholder={t('Reply Text')}
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                            rows={5}
                                        />
                                    </div>
                                    <div className="flex justify-end gap-3 pt-2">
                                        <Button 
                                            onClick={() => navigate('/dashboard/Tickets')}
                                            appearance="subtle"
                                        >
                                            {t('Cancel')}
                                        </Button>
                                        <Button
                                            color="green"
                                            onClick={handleSubmitReply}
                                            disabled={!replyText.trim() || isSubmitting}
                                            className="flex items-center gap-2"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    {t('Loading...')}
                                                </>
                                            ) : (
                                                <>
                                                    <SendIcon className="w-4 h-4" />
                                                    {t('Save Reply')}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <CloseIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">{t('Ticket not found')}</p>
                        <Button 
                            onClick={() => navigate('/dashboard/Tickets')} 
                            className="mt-4"
                            appearance="subtle"
                        >
                            {t('Back to Tickets')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
